/**
 * Mock Pickup Service
 *
 * In-memory mock that mirrors the real pickupService API.
 * Used when VITE_ENABLE_MOCK_DATA=true.
 */

import { mockPickupFolders, mockPickupUsers } from '../data/mockData';

let folders = JSON.parse(JSON.stringify(mockPickupFolders));
let users = JSON.parse(JSON.stringify(mockPickupUsers));
let nextFolderId = 100;
let nextUserId = 100;

const delay = (ms = 200) => new Promise(r => setTimeout(r, ms));

/* ── Pickup Folders ── */

export const getPickupFolders = async (params = {}) => {
  await delay();
  const { search = '', page = 1, limit = 10 } = params;

  let filtered = folders;
  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(f =>
      f.name.toLowerCase().includes(term) ||
      (f.description && f.description.toLowerCase().includes(term))
    );
  }

  const total = filtered.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);

  return { data, pagination: { page, limit, total, totalPages } };
};

export const getAllPickupFolders = async () => {
  await delay();
  return folders.filter(f => f.isActive);
};

export const getPickupFolderById = async (folderId) => {
  await delay();
  const folder = folders.find(f => f.id === folderId);
  if (!folder) throw new Error('Pickup folder not found');
  return { ...folder };
};

export const createPickupFolder = async (data) => {
  await delay(300);
  const newFolder = {
    id: `pf-${nextFolderId++}`,
    name: data.name,
    description: data.description || '',
    s3Prefix: `boxi_pickup/pf-${nextFolderId}/`,
    retentionDays: data.retentionDays || 30,
    isActive: true,
    reportCount: 0,
    userCount: 0,
    createdAt: new Date().toISOString()
  };
  folders.push(newFolder);
  return { data: newFolder };
};

export const updatePickupFolder = async (folderId, data) => {
  await delay(300);
  const folder = folders.find(f => f.id === folderId);
  if (!folder) throw new Error('Pickup folder not found');
  if (data.name !== undefined) folder.name = data.name;
  if (data.description !== undefined) folder.description = data.description;
  if (data.retentionDays !== undefined) folder.retentionDays = data.retentionDays;
  if (data.isActive !== undefined) folder.isActive = data.isActive;
  return { data: { id: folderId } };
};

export const deletePickupFolder = async (folderId) => {
  await delay(300);
  const folder = folders.find(f => f.id === folderId);
  if (!folder) throw new Error('Pickup folder not found');
  if (folder.reportCount > 0) throw new Error('Cannot delete: folder has reports assigned. Remove report assignments first.');
  folders = folders.filter(f => f.id !== folderId);
  return { success: true };
};

/* ── Pickup Users ── */

export const getPickupUsers = async (params = {}) => {
  await delay();
  const { search = '', page = 1, limit = 10 } = params;

  let filtered = users;
  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(u =>
      u.username.toLowerCase().includes(term) ||
      (u.folderNames && u.folderNames.some(n => n.toLowerCase().includes(term)))
    );
  }

  const total = filtered.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);

  return { data, pagination: { page, limit, total, totalPages } };
};

export const getPickupUserById = async (userId) => {
  await delay();
  const user = users.find(u => u.id === userId);
  if (!user) throw new Error('Pickup user not found');
  return { ...user };
};

export const createPickupUser = async (data) => {
  await delay(300);
  const folderNames = (data.folderIds || []).map(fId => {
    const f = folders.find(x => x.id === fId);
    return f ? f.name : fId;
  });
  const newUser = {
    id: `pu-${nextUserId++}`,
    username: data.username,
    folderIds: data.folderIds || [],
    folderNames,
    isActive: true,
    createdAt: new Date().toISOString()
  };
  users.push(newUser);
  // Update folder user counts
  (data.folderIds || []).forEach(fId => {
    const f = folders.find(x => x.id === fId);
    if (f) f.userCount = (f.userCount || 0) + 1;
  });
  return { data: newUser };
};

export const updatePickupUser = async (userId, data) => {
  await delay(300);
  const user = users.find(u => u.id === userId);
  if (!user) throw new Error('Pickup user not found');
  if (data.username !== undefined) user.username = data.username;
  if (data.folderIds !== undefined) {
    user.folderIds = data.folderIds;
    user.folderNames = data.folderIds.map(fId => {
      const f = folders.find(x => x.id === fId);
      return f ? f.name : fId;
    });
  }
  if (data.isActive !== undefined) user.isActive = data.isActive;
  return { data: { id: userId } };
};

export const deletePickupUser = async (userId) => {
  await delay(300);
  const user = users.find(u => u.id === userId);
  if (!user) throw new Error('Pickup user not found');
  // Deactivate rather than hard delete
  user.isActive = false;
  return { success: true };
};

export const getPickupFolderFiles = async (folderId) => {
  await delay();
  const folder = folders.find(f => f.id === folderId);
  if (!folder) throw new Error('Pickup folder not found');
  return {
    files: [
      { key: `boxi_pickup/${folder.name}/Report_A.xlsx`, filename: 'Report_A.xlsx', size: 45678, lastModified: new Date().toISOString() },
      { key: `boxi_pickup/${folder.name}/Report_B.pdf`, filename: 'Report_B.pdf', size: 123456, lastModified: new Date().toISOString() }
    ],
    archivedFiles: {
      '2026/04/14': [
        { key: `boxi_pickup/${folder.name}/archive/2026/04/14/Report_A.xlsx`, filename: 'Report_A.xlsx', size: 44321, lastModified: '2026-04-14T10:00:00+00:00' }
      ]
    },
    total: 2,
    totalArchived: 1,
    folderName: folder.name
  };
};

export const downloadPickupFile = async (folderId, fileKey) => {
  await delay(300);
  const folder = folders.find(f => f.id === folderId);
  if (!folder) throw new Error('Pickup folder not found');
  return {
    downloadUrl: `https://mock-s3.example.com/${fileKey}?signed=true`,
    fileKey,
    expiresIn: 900
  };
};
